import { defineStore } from 'pinia';
import { watch, markRaw } from 'vue';
import { client, getErrorMessage } from '@/api';
import { ElMessage, ElMessageBox } from 'element-plus';
import { debounce } from 'es-toolkit';
import { StopPhraseResponseDto } from '@/api/models/stop-phrase-response.dto';
import { GetStopPhrasesOffsetParameters } from '@/api/models/get-stop-phrases-offset-parameters';
import { ListResponseDto } from '@/api/response/list-response.dto';
import { AddStopPhraseRequestDto } from '@/api/models/add-stop-phrase-request.dto';
import { DeleteStopPhraseParameters } from '@/api/models/delete-stop-phrase-parameters';
import { Delete } from '@element-plus/icons-vue';

export type StopPhrasesState = {
  isWatching: boolean;
  loading: boolean;
  error: string | null;
  search: string | null;

  isAddModalOpened: boolean;

  stopPhrases: StopPhraseResponseDto[];
  stopPhrase: StopPhraseResponseDto | null;

  pagination: {
    page: number;
    pageSize: number;
    total: number;
    pageCount: number;
  };
};

const DEFAULT_PAGINATION = {
  page: 1,
  pageSize: 15,
  total: 0,
  pageCount: 0,
};

const getInitState = () => ({
  isWatching: false,
  loading: false,
  error: null,
  search: null,
  isAddModalOpened: false,
  stopPhrases: [],
  stopPhrase: null,

  pagination: { ...DEFAULT_PAGINATION },
});

export const useStopPhrases = defineStore('stop-phrases', {
  state: (): StopPhrasesState => getInitState(),

  actions: {
    init() {
      const debounceFetch = debounce(() => {
        if (this.isWatching) {
          this.refresh();
        }
      }, 300);

      watch(
        () => [this.search],
        () => debounceFetch(),
        { immediate: true },
      );
    },

    reset() {
      this.$reset();
    },

    async refresh() {
      await this.getStopPhrasesOffset({});
    },

    setLoading(value: boolean) { this.loading = value; },
    setError(value: string | null) { this.error = value; },
    setStopPhrase(stopPhrase: StopPhraseResponseDto | null) { this.stopPhrase = stopPhrase; },

    openAddModal() { this.isAddModalOpened = true; },
    closeAddModal() { this.isAddModalOpened = false; },

    setPagination({ page, limit }: {page: number; limit: number}): void {
      this.pagination = {
        ...this.pagination,
        page,
        pageSize: limit,
      };

      this.getStopPhrasesOffset({});
    },

    async getStopPhrasesOffset(
      query: GetStopPhrasesOffsetParameters = {},
    ): Promise<ListResponseDto<StopPhraseResponseDto>> {
      this.setError(null);
      this.setLoading(true);

      let data: ListResponseDto<StopPhraseResponseDto> = {
        items: [],
        total: 0,
        page: 1,
        pageSize: 10,
        perPage: 10,
        pageCount: 1,
      };

      try {
        data = await client.stopPhrasesApi.getStopPhrasesOffset({
          ...query,
          query: (this.search && this.search.length >= 3) ? this.search : undefined,

          pageNumber: this.pagination.page,
          pageSize: this.pagination.pageSize,
        });

        const { items, ...pagination } = data;

        this.stopPhrases = items ?? [];
        this.pagination = pagination as typeof DEFAULT_PAGINATION;
      } catch (err) {
        const message = getErrorMessage(err, 'Error during getting list of stop phrases');

        ElMessage.error(message);

        this.setError(message);
      } finally {
        this.setLoading(false);
      }

      return data;
    },

    async addStopPhrase(data: AddStopPhraseRequestDto): Promise<StopPhraseResponseDto | null> {
      this.setError(null);
      this.setLoading(true);

      let stopPhrase: StopPhraseResponseDto | null = null;

      try {
        stopPhrase = await client.stopPhrasesApi.addStopPhrase(data);

        this.setStopPhrase(stopPhrase);

        ElMessage.success('Stop phrase was added successfully');

        this.refresh();
      } catch (err) {
        const message = getErrorMessage(err, 'Error during adding a new stop phrase');

        ElMessage.error({
          message,
          duration: 10000,
        });
      } finally {
        this.setLoading(false);
      }

      return stopPhrase;
    },

    async deleteStopPhrase(params: DeleteStopPhraseParameters, phrase: string): Promise<void> {
      try {
        await ElMessageBox.confirm(
          `Please confirm deleting stop phrase <strong>"${phrase}"</strong>`,
          'Delete stop phrase',
          {
            confirmButtonText: 'DELETE',
            cancelButtonText: 'Cancel',
            type: 'error',
            icon: markRaw(Delete),
            dangerouslyUseHTMLString: true,
            confirmButtonClass: 'confirm-delete-button',
          },
        );
      } catch (err) {
        return;
      }

      this.setError(null);
      this.setLoading(true);

      try {
        await client.stopPhrasesApi.deleteStopPhrase(params);

        ElMessage.success('Stop phrase was removed successfully');

        await this.getStopPhrasesOffset();
      } catch (err) {
        const message = getErrorMessage(err, 'Error during adding a new stop phrase');

        ElMessage.error({
          message,
          duration: 10000,
        });
      } finally {
        this.setLoading(false);
      }
    },

    async uploadStopPhrases(formData: { file: File; }): Promise<void> {
      this.setError(null);
      this.setLoading(true);

      try {
        const result = await client.stopPhrasesApi.uploadStopPhrases(formData);

        ElMessage.success({
          message: `Stop phrase was uploaded. Requested ${result.requestedCount}. Processed ${result.processedCount}.`,
          duration: 10000,
        });

        await this.refresh();
      } catch (err) {
        const message = getErrorMessage(err, 'Error during uploading new stop phrases from file');

        ElMessage.error({
          message,
          duration: 10000,
        });
      } finally {
        this.setLoading(false);
      }
    },
  },
});
